import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import { formatDate } from '../../utils/helper';
import { toast } from 'react-toastify';
import { getAppointmentStatus } from '../../utils/statusHelper';

const SpecialistCheckup = () => {
    const { id } = useParams(); // Appointment ID
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('normal'); // 'normal' or 'specialist'

    const [formData, setFormData] = useState({
        remarks: '',
        document: null,
        next_visit_month: '',
        next_visit_year: new Date().getFullYear().toString(),
    });

    useEffect(() => {
        fetchCheckupData();
    }, [id]);

    const fetchCheckupData = async () => {
        setLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/specialist-checkups/data/${id}`,
            });
            if (response.data.status) {
                setData(response.data.data);
            } else {
                toast.error(response.data.message);
                navigate(-1);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching data');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (status) => {
        try {
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/specialist-checkups/status/${id}`,
                data: { status }
            });
            if (response.data.status) {
                toast.success(response.data.message);
                fetchCheckupData(); // Refresh status
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error updating status');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 500 * 1024) { // 500KB
                toast.error('File size exceeds 500KB');
                e.target.value = null;
                return;
            }
            setFormData({ ...formData, document: file });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const dataPayload = new FormData();
        dataPayload.append('appointment_id', id);
        dataPayload.append('remarks', formData.remarks);
        if (formData.document) {
            dataPayload.append('document', formData.document);
        }
        dataPayload.append('next_visit_month', formData.next_visit_month);
        dataPayload.append('next_visit_year', formData.next_visit_year);

        try {
            const response = await ApiService.request({
                method: 'POST',
                url: 'admin/specialist-checkups',
                data: dataPayload,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.status) {
                toast.success(response.data.message);
                // Redirect back to visit details. We need to find the visit ID.
                // Since we don't have direct link, we can go back to history or find a way.
                // The user likely came from the details page.
                navigate(-1); 
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error completing checkup');
        }
    };

    if (loading) return <Loader />;
    if (!data) return <div>Data not found</div>;

    const { appointment, last_normal_checkup, last_specialist_checkup, current_specialist_checkup } = data;
    const doctorData = last_normal_checkup?.doctor_records || last_normal_checkup?.receptionist_records || {};

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    
                    {/* Patient Info Header */}
                    <div className="card mb-5 mb-xl-10">
                        <div className="card-body pt-9 pb-0">
                            <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
                                <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                                        <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center mb-2">
                                                <span className="text-gray-900 text-hover-primary fs-2 fw-bold me-1">
                                                    {appointment.patient?.name}
                                                </span>
                                                <span className={`badge badge-light-${appointment.status === 'pending' ? 'info' : (appointment.status === 'completed' || appointment.status === 'checkup_completed') ? 'success' : 'warning'} fw-bold ms-2 fs-8 py-1 px-3`}>
                                                    {appointment.status === 'pending' ? 'In Progress' : appointment.status === 'checkup_completed' ? 'Completed' : appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                                                </span>
                                            </div>
                                            <div className="d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2">
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-phone me-1"></i> {appointment.patient?.phone}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 me-5 mb-2">
                                                    <i className="fas fa-venus-mars me-1"></i> {appointment.patient?.gender || 'N/A'}
                                                </span>
                                                <span className="d-flex align-items-center text-gray-400 mb-2">
                                                    <i className="fas fa-credit-card me-1"></i> {appointment.payment_method ? appointment.payment_method.charAt(0).toUpperCase() + appointment.payment_method.slice(1) : 'N/A'}
                                                </span>
                                                {/* <span className="d-flex align-items-center text-gray-400 mb-2">
                                                    <i className="fas fa-birthday-cake me-1"></i> {appointment.patient?.age || 'N/A'} Yrs
                                                </span> */}
                                            </div>
                                        </div>
                                        <div className="d-flex my-4">
                                            {appointment.status === 'queue' && (
                                                <button 
                                                    className="btn btn-sm btn-primary me-2"
                                                    onClick={() => handleStatusChange('pending')}
                                                >
                                                    Accept Patient
                                                </button>
                                            )}
                                            {appointment.status === 'pending' && (
                                                <button 
                                                    className="btn btn-sm btn-warning me-2"
                                                    onClick={() => handleStatusChange('queue')}
                                                >
                                                    Send Back to Queue
                                                </button>
                                            )}
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => navigate(-1)}
                                            >
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-5 g-xl-10">
                        {/* History Column */}
                        <div className="col-xl-6">
                            <div className="card mb-5 mb-xl-10" style={{height: '100%'}}>
                                <div className="card-header">
                                    <div className="card-title m-0">
                                        <h3 className="fw-bold m-0">Patient History</h3>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <ul className="nav nav-tabs nav-line-tabs mb-5 fs-6">
                                        <li className="nav-item">
                                            <a 
                                                className={`nav-link cursor-pointer ${activeTab === 'normal' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('normal')}
                                            >
                                                Last Normal Checkup
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a 
                                                className={`nav-link cursor-pointer ${activeTab === 'specialist' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('specialist')}
                                            >
                                                Last Specialist Visit
                                            </a>
                                        </li>
                                    </ul>

                                    <div className="tab-content" style={{maxHeight: '600px', overflowY: 'auto'}}>
                                        {activeTab === 'normal' && (
                                            <div className="tab-pane fade show active">
                                                {last_normal_checkup ? (
                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                                            <div>
                                                                <label className="fw-bold text-muted">Date:</label>
                                                                <span className="fw-bold fs-6 ms-2">{formatDate(last_normal_checkup.date)}</span>
                                                            </div>
                                                            <div>
                                                                <label className="fw-bold text-muted">Doctor:</label>
                                                                <span className="badge badge-light-primary ms-2">{last_normal_checkup.doctor?.name}</span>
                                                            </div>
                                                        </div>

                                                        <h6 className="mb-2 text-muted">Auto Refraction (A/R)</h6>
                                                        <table className="table table-bordered table-sm align-middle mb-4">
                                                            <thead>
                                                                <tr className="fw-bold fs-7 text-gray-800 bg-light">
                                                                    <th>Eye</th>
                                                                    <th>Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="fs-7">
                                                                <tr>
                                                                    <td className="fw-bold bg-light">R.E</td>
                                                                    <td>{doctorData.ar_re || '-'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold bg-light">L.E</td>
                                                                    <td>{doctorData.ar_le || '-'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                        <h6 className="mb-2 text-muted">Rx</h6>
                                                        <table className="table table-bordered table-sm align-middle mb-4">
                                                            <thead>
                                                                <tr className="fw-bold fs-7 text-gray-800 bg-light">
                                                                    <th>Eye</th>
                                                                    <th>SPH</th>
                                                                    <th>CYL</th>
                                                                    <th>AXIS</th>
                                                                    <th>VA</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="fs-7">
                                                                <tr>
                                                                    <td className="fw-bold">R.E</td>
                                                                    <td>{doctorData.rx_re_sph || '-'}</td>
                                                                    <td>{doctorData.rx_re_cyl || '-'}</td>
                                                                    <td>{doctorData.rx_re_axis || '-'}</td>
                                                                    <td>{doctorData.rx_re_va || '-'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="fw-bold">L.E</td>
                                                                    <td>{doctorData.rx_le_sph || '-'}</td>
                                                                    <td>{doctorData.rx_le_cyl || '-'}</td>
                                                                    <td>{doctorData.rx_le_axis || '-'}</td>
                                                                    <td>{doctorData.rx_le_va || '-'}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>

                                                        <div className="row mb-4">
                                                            <div className="col-6">
                                                                <h6 className="mb-2 text-muted">PD</h6>
                                                                <table className="table table-bordered table-sm align-middle">
                                                                    <thead>
                                                                        <tr className="fw-bold fs-7 text-gray-800 bg-light">
                                                                            <th>R</th>
                                                                            <th>L</th>
                                                                            <th>Total</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="fs-7">
                                                                        <tr>
                                                                            <td>{doctorData.pd_re || '-'}</td>
                                                                            <td>{doctorData.pd_le || '-'}</td>
                                                                            <td>{doctorData.pd_total || '-'}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            <div className="col-6">
                                                                <h6 className="mb-2 text-muted">Lensometer</h6>
                                                                <table className="table table-bordered table-sm align-middle">
                                                                    <thead>
                                                                        <tr className="fw-bold fs-7 text-gray-800 bg-light">
                                                                            <th>R.E</th>
                                                                            <th>L.E</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="fs-7">
                                                                        <tr>
                                                                            <td>{doctorData.lensometer_re || '-'}</td>
                                                                            <td>{doctorData.lensometer_le || '-'}</td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>

                                                        <div className="mb-4">
                                                            <label className="fw-bold text-muted">Findings/Notes:</label>
                                                            <div className="p-3 bg-light rounded text-gray-800 fs-7">
                                                                {doctorData.doctor_findings || 'No notes'}
                                                            </div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <label className="fw-bold text-muted fs-7">Specialist Rec:</label>
                                                            <span className="ms-2 fs-7 fw-bold text-gray-800">
                                                                {doctorData.specialist_recommendation || 'No'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center text-muted p-5">No previous normal checkup found.</div>
                                                )}
                                            </div>
                                        )}
                                        {activeTab === 'specialist' && (
                                            <div className="tab-pane fade show active">
                                                {last_specialist_checkup ? (
                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                                            <div>
                                                                <label className="fw-bold text-muted">Date:</label>
                                                                <span className="fw-bold fs-6 ms-2">{formatDate(last_specialist_checkup.created_at)}</span>
                                                            </div>
                                                            <div>
                                                                <label className="fw-bold text-muted">Specialist:</label>
                                                                <span className="badge badge-light-info ms-2">{last_specialist_checkup.specialist?.name}</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="mb-4">
                                                            <label className="fw-bold text-muted">Remarks:</label>
                                                            <div className="p-3 bg-light rounded text-gray-800 fs-6">
                                                                {last_specialist_checkup.remarks || 'No remarks'}
                                                            </div>
                                                        </div>

                                                        {(last_specialist_checkup.next_visit_month || last_specialist_checkup.next_visit_year) && (
                                                            <div className="mb-4">
                                                                <label className="fw-bold text-muted">Next Visit:</label>
                                                                <span className="fw-bold fs-6 ms-2">
                                                                    {last_specialist_checkup.next_visit_month} {last_specialist_checkup.next_visit_year}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {last_specialist_checkup.document_path && (
                                                            <div className="mb-4">
                                                                <label className="fw-bold text-muted">Document:</label>
                                                                <div className="mt-2">
                                                                    <a href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/storage/${last_specialist_checkup.document_path}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light-primary">
                                                                        <i className="fas fa-file-download me-2"></i> View Document
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="text-center text-muted p-5">No previous specialist visit found.</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Column */}
                        <div className="col-xl-6">
                            <div className="card mb-5 mb-xl-10" style={{height: '100%'}}>
                                <div className="card-header">
                                    <div className="card-title m-0">
                                        <h3 className="fw-bold m-0">Checkup Details</h3>
                                    </div>
                                </div>
                                {appointment.status === 'queue' ? (
                                    <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-10">
                                        <i className="fas fa-user-clock fs-3x text-warning mb-5"></i>
                                        <h3 className="fw-bold text-gray-800 mb-2">Patient Waiting</h3>
                                        <div className="text-gray-600 fs-6 mb-5">
                                            Please accept the patient to start the checkup process.
                                        </div>
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => handleStatusChange('pending')}
                                        >
                                            Accept Patient
                                        </button>
                                    </div>
                                ) : (appointment.status === 'completed' || appointment.status === 'checkup_completed') ? (
                                    <div className="card-body">
                                        <div className="alert alert-success d-flex align-items-center p-5 mb-10">
                                            <i className="fas fa-check-circle fs-2hx text-success me-4"></i>
                                            <div className="d-flex flex-column">
                                                <h4 className="mb-1 text-success">Checkup Completed</h4>
                                                <span>This checkup has been finalized.</span>
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <label className="fw-bold text-muted mb-2">Remarks:</label>
                                            <div className="p-4 bg-light rounded text-gray-800 fs-6">
                                                {current_specialist_checkup?.remarks || 'No remarks provided.'}
                                            </div>
                                        </div>

                                        {current_specialist_checkup?.document_path && (
                                            <div className="mb-6">
                                                <label className="fw-bold text-muted mb-2">Uploaded Document:</label>
                                                <div>
                                                    <a 
                                                        href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/storage/${current_specialist_checkup.document_path}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        className="btn btn-light-primary btn-sm"
                                                    >
                                                        <i className="fas fa-file-download me-2"></i> View Document
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        {(current_specialist_checkup?.next_visit_month || current_specialist_checkup?.next_visit_year) && (
                                            <div className="mb-6">
                                                <label className="fw-bold text-muted mb-2">Next Specialist Clinic:</label>
                                                <div className="fw-bold fs-6">
                                                    {current_specialist_checkup.next_visit_month} {current_specialist_checkup.next_visit_year}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="card-body">
                                        <div className="mb-6">
                                            <label className="required form-label">Remarks</label>
                                            <textarea 
                                                className="form-control form-control-solid" 
                                                rows="5"
                                                value={formData.remarks}
                                                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                                                required
                                            ></textarea>
                                        </div>

                                        <div className="mb-6">
                                            <label className="form-label">Upload Document/Image (Optional, Max 500KB)</label>
                                            <input 
                                                type="file" 
                                                className="form-control form-control-solid" 
                                                accept="image/*,.pdf,.doc,.docx"
                                                onChange={handleFileChange}
                                            />
                                            <div className="form-text">Allowed file types: png, jpg, jpeg, pdf. Max size: 500KB.</div>
                                        </div>

                                        <div className="mb-6">
                                            <label className="form-label">Next Specialist Clinic</label>
                                            <div className="row">
                                                <div className="col-6">
                                                    <select 
                                                        className="form-select form-select-solid"
                                                        value={formData.next_visit_month}
                                                        onChange={(e) => setFormData({...formData, next_visit_month: e.target.value})}
                                                    >
                                                        <option value="">Select Month</option>
                                                        {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-6">
                                                    <select 
                                                        className="form-select form-select-solid"
                                                        value={formData.next_visit_year}
                                                        onChange={(e) => setFormData({...formData, next_visit_year: e.target.value})}
                                                    >
                                                        <option value="">Select Year</option>
                                                        {[0, 1, 2, 3].map(i => {
                                                            const y = new Date().getFullYear() + i;
                                                            return <option key={y} value={y}>{y}</option>
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end pt-5">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary"
                                                disabled={appointment.status === 'checkup_completed'}
                                            >
                                                <span className="indicator-label">Complete Checkup</span>
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SpecialistCheckup;
